import type { StyleRow } from './stylesFixtureData';
import { seasons, groups, brandOptions, suppliers, contactPersons } from './stylesFixtureData';
import { FIT_TYPE_REGULAR, FIT_TYPE_SLIM } from './stylesMultiFilterFixtures';

/**
 * Fixtures for the DL-66657 epic (dl-57695, Testmo folder 93168, part 6 of 9 — filter state URL
 * persistence, the API parameter contract for the `customId`/`stylecolor` renames, and custom-field
 * filter types incl. Numeric). Real, persisted `stylesData` rows — NOT `apiTransform` overrides —
 * so every filter/persistence spec exercises the genuine `applyFilters`/`applyCustomFieldFilters`
 * path (an injected/overridden row bypasses that handler entirely — DL-64371).
 *
 * Additive only: appended to `stylesData` on top of every existing generated + sibling-epic
 * fixture row. Nothing existing is edited. Ids 99000+ reserved for this epic (checked empirically
 * — the highest id in use elsewhere at time of writing is DL-66644's ~98376).
 *
 * **Verified divergences from the epic's literal prose** (same discipline DL-66605/DL-66644
 * established — read source, don't assume the Testmo/epic text):
 *  - The base 150-row generated fixture (`stylesGeneratedFixtureData.ts`) seeds every row with
 *    `styleColors: []` — NO style carries a color by default — so the Style Color filter/rename
 *    stories need their own real, color-bearing rows (below), not the generated set.
 *  - `getCustomFieldColumnId` (`generateColumns.ts`) composes a Numeric CF's id as `cf_<id>_number`
 *    — never `cf_<id>_num` as the epic's own literal example writes it.
 *  - `transforms.ts`'s `getValueForUrl` joins a `number`-type filter's range as `${from}_${to}` —
 *    an UNDERSCORE, not the comma the epic's literal example shows (`cf_43_num=5,20`). A CF Date
 *    filter (`dateRange` type) DOES use a comma, matching the standard `lastUpdated` scheme — only
 *    Numeric uses the underscore. Both are asserted structurally (parsed, never string-equality)
 *    per this epic's own cross-cutting hazard #1.
 *
 * Row groups:
 *  - **Style Color** (ids 99000-99002) — DL-66659/66660/66661/66662/66663 (customId/stylenumber
 *    URL persistence, stylecolorname param rename + persistence, legacy-URL behavior, cardinality).
 *    "Navy Blue" / "Navy Blue Trim" (both a genuine substring match) / "Forest Green" (decoy).
 *  - **CF combination matrix** (ids 99010-99012) — DL-66665 (Text+Date+AllowedValue three-way AND
 *    — the decoy row deliberately shares BOTH text AND date with the target row, so only adding
 *    the third condition narrows to one), DL-66666 (Numeric CF AND'd with a standard Season
 *    filter), DL-66667 (CF URL persistence incl. Numeric), DL-66668 (clear-all removes CF filters
 *    + URL params).
 */

let nextFixtureId = 99000;
const nextId = (): number => nextFixtureId++;

const baseRow = (overrides: Partial<StyleRow> & { name: string; customId: string }): StyleRow => ({
  id: nextId(),
  description: null,
  state: 'published',
  logo: '',
  season: seasons[0],
  group: groups[0],
  brand: {
    id: brandOptions[0].id,
    customId: brandOptions[0].userDefinedId ?? '',
    name: brandOptions[0].name,
    contactPersonId: contactPersons[0].id,
    contactPersonName: contactPersons[0].name,
  },
  supplier: {
    id: suppliers[0].id,
    customId: suppliers[0].customId,
    name: suppliers[0].name,
    contactPersonId: contactPersons[1].id,
    contactPersonName: contactPersons[1].name,
  },
  categories: [],
  styleColors: [],
  customFields: [],
  properties: [],
  lastUpdated: '2026-04-01T09:00:00',
  readyForExport: false,
  ...overrides,
});

// ---------------------------------------------------------------------------------------------
// Style Color rows — DL-66659/66660/66661/66662/66663. Names/customIds prefixed "SFUC-CLR" so a
// spec can resolve rows by identity, never by index/position.
// ---------------------------------------------------------------------------------------------
export const STYLE_COLOR_ROW_NAMES = {
  navyPrimary: 'Filter Contract Navy Primary',
  navySecondary: 'Filter Contract Navy Secondary',
  forestDecoy: 'Filter Contract Forest Decoy',
};

// ---------------------------------------------------------------------------------------------
// Style Number row — DL-66659 (`customId`/`stylenumber` URL-persistence story), a deterministic,
// collision-free customId distinct from the generated `ST-100x` range.
// ---------------------------------------------------------------------------------------------
export const STYLE_NUMBER_PROBE_CUSTOM_ID = 'SFUC-NUM-4242';

// ---------------------------------------------------------------------------------------------
// CF combination matrix — DL-66665/66666/66667/66668. See header for the weight/season pairing
// rationale (proves AND, not OR — no two columns share the same 2-row match set).
// ---------------------------------------------------------------------------------------------
export const CF_COMBO_CUSTOM_IDS = {
  cottonMayRegular: 'SFUC-CF-01', // CF701 "cotton", CF702 May 2026, CF703 Regular, CF708 150, AW25
  denimJuneSlim: 'SFUC-CF-02', // CF701 "denim", CF702 June 2026, CF703 Slim, CF708 400, AW25
  cottonMaySlimDecoy: 'SFUC-CF-03', // CF701 "cotton" (shares w/ 01), CF702 May (shares w/ 01), CF703 Slim (differs), CF708 180, AW26
};

export const buildFilterUrlContractFixtureStyles = (): StyleRow[] => [
  baseRow({
    name: STYLE_COLOR_ROW_NAMES.navyPrimary,
    customId: 'SFUC-CLR-01',
    styleColors: [{ id: 90001, name: 'Navy Blue', customId1: '', customId2: '' }],
  }),
  baseRow({
    name: STYLE_COLOR_ROW_NAMES.navySecondary,
    customId: 'SFUC-CLR-02',
    // A row can carry more than one color; "Navy Blue Trim" is a genuine substring match on
    // "Navy Blue" (proves partial, not exact-equality, matching), "Cream" is a second, unrelated
    // color on the SAME row (proves the match checks every color, not just the first).
    styleColors: [
      { id: 90002, name: 'Navy Blue Trim', customId1: '', customId2: '' },
      { id: 90003, name: 'Cream', customId1: '', customId2: '' },
    ],
  }),
  baseRow({
    name: STYLE_COLOR_ROW_NAMES.forestDecoy,
    customId: 'SFUC-CLR-03',
    styleColors: [{ id: 90004, name: 'Forest Green', customId1: '', customId2: '' }],
  }),

  baseRow({
    name: 'Filter Contract Number Probe',
    customId: STYLE_NUMBER_PROBE_CUSTOM_ID,
  }),

  baseRow({
    name: 'CF Contract Cotton May Regular',
    customId: CF_COMBO_CUSTOM_IDS.cottonMayRegular,
    season: seasons[0], // AW25
    customFields: [
      { id: 701, type: 'text', value: { id: 0, value: 'Soft touch cotton blend' } },
      { id: 702, type: 'date', value: { id: 0, value: '2026-05-15' } },
      {
        id: 703,
        type: 'allowedvalue',
        value: { id: FIT_TYPE_REGULAR, value: String(FIT_TYPE_REGULAR) },
      },
      { id: 708, type: 'numeric', value: { id: 0, value: '150' } },
    ],
  }),
  baseRow({
    name: 'CF Contract Denim June Slim',
    customId: CF_COMBO_CUSTOM_IDS.denimJuneSlim,
    season: seasons[0], // AW25 — same season as cottonMayRegular
    customFields: [
      { id: 701, type: 'text', value: { id: 0, value: 'Rough denim weave' } },
      { id: 702, type: 'date', value: { id: 0, value: '2026-06-20' } },
      { id: 703, type: 'allowedvalue', value: { id: FIT_TYPE_SLIM, value: String(FIT_TYPE_SLIM) } },
      { id: 708, type: 'numeric', value: { id: 0, value: '400' } },
    ],
  }),
  baseRow({
    name: 'CF Contract Cotton May Slim Decoy',
    customId: CF_COMBO_CUSTOM_IDS.cottonMaySlimDecoy,
    season: seasons[1], // AW26 — different season from both rows above
    customFields: [
      // Shares BOTH the "cotton" substring AND the May 2026 date with cottonMayRegular — a
      // text+date filter alone still admits this row too. Only adding the THIRD condition
      // (CF703 Fit Type = Regular) narrows to cottonMayRegular alone — the genuine three-way AND
      // proof DL-66665 needs (a two-condition filter is deliberately NOT already singleton).
      { id: 701, type: 'text', value: { id: 0, value: 'Soft touch cotton lining' } },
      { id: 702, type: 'date', value: { id: 0, value: '2026-05-22' } },
      { id: 703, type: 'allowedvalue', value: { id: FIT_TYPE_SLIM, value: String(FIT_TYPE_SLIM) } },
      { id: 708, type: 'numeric', value: { id: 0, value: '180' } },
    ],
  }),
];
